import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as ExportIcon } from '@/assets/icons/export.svg';
import Button from '@/components/Button';
import ChannelsFilter from '@/components/ChannelsFilter';
import { Controls, ControlsButton } from '@/components/Controls';
import HideElements from '@/components/HideElements';
import { Sections } from '@/components/Layout/Parts';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useChannelsFilter from '@/hooks/useChannelsFilter';
import { Dot } from '@/pages/OptimisationProject/components/OptimisationStatusBadge';
import { PerformanceGroup, Period } from '@/types/resources';
import { api } from '@/utils/api';
import { download } from '@/utils/helpers';

import PerformanceIndexGroup from './PerformanceIndexGroup';

const UpdatedTime = styled.div`
  height: 32px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  padding: 0 16px;
  margin-right: auto;
  ${({ theme }) => css`
    background: ${theme.colors.white};
  `}
  ${Text} {
    margin-left: 8px;
  }
`;

const performanceGroups: Array<PerformanceGroup> = [
  'quantitive',
  'qualitative',
  'competitive',
  'financial',
];

const periods: Array<Period> = [
  '1 day',
  '7 days',
  '1 month',
  '3 months',
  '1 year',
];

const Header = styled.div`
  display: flex;
  margin: 0 0 12px;
  justify-content: flex-end;
  ${Controls} {
    margin-left: 16px;
  }
`;

const StyledChannelsFilter = styled(ChannelsFilter)`
  margin: 0;
`;

const PerformanceIndexes: FC = () => {
  const [activePeriod, setActivePeriod] = useState<Period>(periods[1]);
  const [isExporting, setIsExporting] = useState(false);

  const { teamId } = useParams();

  const alerts = useAlerts();

  const { data } = useQuery(['performance_updated_at', { teamId }], () =>
    api.get<never, { updatedAt: string }>(
      `/v1/teams/${teamId}/performance/last-update`,
    ),
  );

  const [filteredChannelsIds] = useChannelsFilter();

  const onExport = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to export it as CSV',
      );

      if (!confirmed) return;

      setIsExporting(true);
      const csv = await api.get<never, string>(
        `/v1/teams/${teamId}/performance/export`,
        {
          params: {
            period: activePeriod,
            ...(filteredChannelsIds?.length && {
              'filter[channel_id]': filteredChannelsIds.join(','),
            }),
          },
        },
      );
      download(csv, `${activePeriod.replace(' ', '-')}.csv`);
      setIsExporting(false);
      alerts.close();
    } catch (err) {
      alerts.error();
      setIsExporting(false);
    }
  };

  const groupsFilter = (group: PerformanceGroup) => !(group === 'financial');

  return (
    <Sections $gapY={48}>
      <div>
        <Header>
          {!!data?.updatedAt && (
            <UpdatedTime>
              <Dot $color="green" />
              <Text as="span" $type="smallBold">
                Updated {DateTime.fromISO(data!.updatedAt).toRelative()}
              </Text>
            </UpdatedTime>
          )}
          <HideElements>
            <Button
              color="secondary"
              small
              onClick={onExport}
              disabled={!data}
              loading={isExporting}
            >
              <ExportIcon />
              Export as CSV
            </Button>
          </HideElements>
          <Controls>
            {periods.map((period, index) => (
              <ControlsButton
                key={index}
                active={activePeriod === period}
                onClick={() => setActivePeriod(period)}
              >
                {period}
              </ControlsButton>
            ))}
          </Controls>
        </Header>
        <StyledChannelsFilter />
      </div>
      {performanceGroups.filter(groupsFilter).map((group, index) => (
        <PerformanceIndexGroup
          key={index}
          group={group}
          period={activePeriod}
        />
      ))}
    </Sections>
  );
};

export default PerformanceIndexes;
